import { useRef, useState } from "react";

import axios from "axios";
import { ResolvedCustomSignUpBlock } from "resolvers/data/customSignUpBlockResolver";
import fetchRecaptchaToken from "services/google/fetchRecaptchaTokenService";
import { logError } from "utils/logger";

type FormSubmissionType<T> = {
    formData: T;
    templates: {
        emailTemplateName?: string;
    } & Pick<
        ResolvedCustomSignUpBlock,
        "customerEmailTemplateName" | "internalEmailAddress" | "internalEmailTemplateName"
    >;
};

const useCustomSignUpBlockFormSubmission = <T extends { email: string }>() => {
    const isFormSubmissionSuccessful = useRef<boolean>(false);
    const [recaptchaToken, setRecaptchaToken] = useState("");

    const handleFormSubmission = async ({ formData, templates }: FormSubmissionType<T>) => {
        try {
            const recaptchaTokenResponse = await fetchRecaptchaToken();
            if (!recaptchaTokenResponse.success) {
                throw new Error("Failed to fetch recaptcha token");
            }

            const token = recaptchaTokenResponse.token;

            const recaptchaVerificationResponse = await axios.post("/api/recaptcha", { token });
            if (
                recaptchaVerificationResponse.status !== 200 ||
                !recaptchaVerificationResponse.data.success
            ) {
                isFormSubmissionSuccessful.current = false;
                throw new Error("Failed to verify recaptcha token");
            }
            if (!!token) {
                setRecaptchaToken(token);
            }

            const {
                emailTemplateName,
                customerEmailTemplateName,
                internalEmailAddress,
                internalEmailTemplateName,
            } = templates;

            if (emailTemplateName || customerEmailTemplateName) {
                const response = await axios.post("/api/email", {
                    email: formData.email,
                    emailTemplateName: customerEmailTemplateName ?? emailTemplateName,
                    additionalVariables: formData,
                });
                if (response.status !== 200) {
                    isFormSubmissionSuccessful.current = false;
                    throw new Error("Failed to send email");
                }
            }

            if (internalEmailAddress && internalEmailTemplateName) {
                const response = await axios.post("/api/email", {
                    email: internalEmailAddress,
                    emailTemplateName: internalEmailTemplateName,
                    additionalVariables: formData,
                });
                if (response.status !== 200) {
                    isFormSubmissionSuccessful.current = false;
                    throw new Error("Failed to send internal email");
                }
            }

            isFormSubmissionSuccessful.current = true;
        } catch (error) {
            const errorMessage = (error as Error).message;
            logError("Error occured submitting the form", errorMessage);

            throw new CustomError("Error occured submitting the form", error);
        }
    };

    return {
        isFormSubmissionSuccessful,
        handleFormSubmission,
        recaptchaToken,
    };
};

export default useCustomSignUpBlockFormSubmission;

class CustomError extends Error {
    public e?: unknown;

    constructor(message: string, e?: unknown) {
        super(message);

        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, CustomError);
        }

        this.name = this.constructor.name;

        if (e !== undefined) {
            this.e = e;
        }
    }
}
